var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Errors } from "../toolkit.config";
import { Config } from "../enums/config.enum";
/**
 * @function getContentNonRefFieldsFromConfig
 * @description **extracts non reference type fields of content type from config.**
 * - config file contains all the content type specific field's machine names.
 * - the non reference type fields are the ones which are not paragraph type field.
 * - the reference type field is the one which is paragraph type field.
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @returns {Promise<string[]>} promise of array of non reference type fields of content type.
 */
export const getContentNonRefFieldsFromConfig = (contentTypeMachineName) => __awaiter(void 0, void 0, void 0, function* () {
    const contentConfig = yield getContentConfig();
    const pageData = contentConfig.metadata[contentTypeMachineName];
    const pageMachineNames = pageData === null || pageData === void 0 ? void 0 : pageData.machineNames;
    const nonReferenceFields = [];
    pageMachineNames === null || pageMachineNames === void 0 ? void 0 : pageMachineNames.forEach((machineName) => {
        if (!(machineName === null || machineName === void 0 ? void 0 : machineName.isReferenceType) &&
            !(machineName === null || machineName === void 0 ? void 0 : machineName.isMediaType) &&
            !(machineName === null || machineName === void 0 ? void 0 : machineName.isTaxonomyType)) {
            nonReferenceFields.push(machineName.machineName);
        }
    });
    return nonReferenceFields;
});
/**
 * @function getContentRefFieldFromConfig
 * @description **extracts reference type field of content type from config.**
 * - config file contains all the content type specific field's machine names.
 * - the non reference type fields are the ones which are not paragraph type field.
 * - the reference type field is the one which is paragraph type field.
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @returns {Promise<string>} promise of reference type field of content type.
 */
export const getContentRefFieldFromConfig = (contentTypeMachineName) => __awaiter(void 0, void 0, void 0, function* () {
    const contentConfig = yield getContentConfig();
    const pageData = contentConfig.metadata[contentTypeMachineName];
    const pageMachineNamesObject = pageData === null || pageData === void 0 ? void 0 : pageData.machineNames;
    let referenceField;
    pageMachineNamesObject === null || pageMachineNamesObject === void 0 ? void 0 : pageMachineNamesObject.forEach((machineNameObject) => {
        if (machineNameObject === null || machineNameObject === void 0 ? void 0 : machineNameObject.isReferenceType) {
            referenceField = machineNameObject.machineName;
        }
    });
    return referenceField;
});
/**
 * @function getContentRefFieldFromConfig
 * @description **extracts media type fields of content type from config.**
 * - config file contains all the content type specific field's machine names.
 * - the non reference type fields are the ones which are not paragraph type field.
 * - the reference type field is the one which is paragraph type field.
 * - the media type field is the one which is media type field.
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @returns {Promise<string[]>} promise of media type fields of content type.
 */
export const getContentMediaFieldFromConfig = (contentTypeMachineName) => __awaiter(void 0, void 0, void 0, function* () {
    const contentConfig = yield getContentConfig();
    const pageData = contentConfig.metadata[contentTypeMachineName];
    const pageMachineNames = pageData === null || pageData === void 0 ? void 0 : pageData.machineNames;
    const mediaFields = [];
    pageMachineNames === null || pageMachineNames === void 0 ? void 0 : pageMachineNames.forEach((machineName) => {
        if (machineName === null || machineName === void 0 ? void 0 : machineName.isMediaType) {
            mediaFields.push(machineName.machineName);
        }
    });
    return mediaFields;
});
/**
 * @function getContentRefFieldFromConfig
 * @description **extracts taxonomy type fields of content type from config.**
 * - config file contains all the content type specific field's machine names.
 * - the non reference type fields are the ones which are not paragraph type field.
 * - the reference type field is the one which is paragraph type field.
 * - the taxonomy type field is the one which is taxonomy type field.
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @returns {Promise<string[]>} promise of taxonomy type fields of content type.
 */
export const getContentTaxonomyFieldFromConfig = (contentTypeMachineName) => __awaiter(void 0, void 0, void 0, function* () {
    const contentConfig = yield getContentConfig();
    const pageData = contentConfig.metadata[contentTypeMachineName];
    const pageMachineNames = pageData === null || pageData === void 0 ? void 0 : pageData.machineNames;
    const taxonomyFields = [];
    pageMachineNames === null || pageMachineNames === void 0 ? void 0 : pageMachineNames.forEach((machineName) => {
        if (machineName === null || machineName === void 0 ? void 0 : machineName.isTaxonomyType) {
            taxonomyFields.push(machineName.machineName);
        }
    });
    return taxonomyFields;
});
/**
 * @function processJsonResponseForParagraphs
 * @description **extracts paragraphs used in content from JSON response.**
 * - the JSON response from CMS contains all the paragraphs used in the content.
 * - this extracts the paragraphs used in the content in the order they are used.
 * @param {string} paragraphFieldMachineName - machine name of the content reference type paragraph field.
 * @param {object} initialContentFromCMS - JSON response from the CMS without media data.
 * @param {number} currentIndex - index of the content in the JSON response.
 * @returns {object[]} array of paragraphs used in content with paragraph id.
 */
export const processJsonResponseForParagraphs = (paragraphFieldMachineName, initialContentFromCMS, currentIndex) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const paragraphsUsedInOrder = [];
    const processedInitialContentFromCMS = (_a = ((initialContentFromCMS === null || initialContentFromCMS === void 0 ? void 0 : initialContentFromCMS.data) instanceof Array
        ? initialContentFromCMS.data[currentIndex]
        : initialContentFromCMS.data)) !== null && _a !== void 0 ? _a : {};
    paragraphFieldMachineName !== undefined &&
        typeof ((_d = (_c = (_b = processedInitialContentFromCMS.relationships) === null || _b === void 0 ? void 0 : _b[paragraphFieldMachineName]) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.length) === "number" &&
        ((_g = (_f = (_e = processedInitialContentFromCMS.relationships) === null || _e === void 0 ? void 0 : _e[paragraphFieldMachineName]) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.forEach((paragraph) => {
            paragraphsUsedInOrder.push({ type: paragraph === null || paragraph === void 0 ? void 0 : paragraph.type, id: paragraph === null || paragraph === void 0 ? void 0 : paragraph.id });
        }));
    return paragraphsUsedInOrder;
};
/**
 * @function processIncludedForChildParagraphs
 * @description **extracts child paragraphs used in content from JSON response.**
 * - the JSON response from CMS contains all the paragraphs used in the content.
 * - this extracts the child paragraphs used in the content in the order they are used.
 * @param {string} parentParaType - type of the parent paragraph.
 * @param {string} parentPareId - id of the parent paragraph.
 * @param {string} machineName - machine name of the child paragraph.
 * @param {object[]} included - included array from JSON response.
 * @returns {object[]} array of child paragraphs used in content with paragraph id.
 */
export const processIncludedForChildParagraphs = (parentParaType, parentPareId, machineName, included) => {
    var _a, _b, _c;
    const childParagraphs = [];
    const parentPara = included.find((include) => include.type === parentParaType && include.id === parentPareId);
    if (!parentPara)
        return [];
    const childParaIncludedRecords = (_c = (_b = (_a = parentPara.relationships) === null || _a === void 0 ? void 0 : _a[machineName]) === null || _b === void 0 ? void 0 : _b.data) !== null && _c !== void 0 ? _c : [];
    childParaIncludedRecords instanceof Array &&
        (childParaIncludedRecords === null || childParaIncludedRecords === void 0 ? void 0 : childParaIncludedRecords.forEach((include) => {
            childParagraphs.push({
                type: include.type,
                id: include.id,
            });
        }));
    return childParagraphs;
};
/**
 * @function processStaticContentConfigForParagraphs
 * @description **extracts paragraphs used in content from config.**
 * - static content config contains all the paragraphs used in the content.
 * - this extracts the paragraphs used in the static content in the order they are used.
 * - static content can not add or remove a new paragraph type.
 * @param {string} paragraphFieldMachineName - machine name of the content reference type paragraph field.
 * @param {object} staticContents - static content config.
 * @returns {object[]} array of paragraphs used in content without paragraph id.
 */
export const processStaticContentConfigForParagraphs = (paragraphFieldMachineName, staticContents) => {
    var _a;
    const staticIncludes = [];
    (_a = staticContents[paragraphFieldMachineName]) === null || _a === void 0 ? void 0 : _a.forEach((paragraphMachineName) => {
        staticIncludes.push({ type: paragraphMachineName, id: "" });
    });
    return staticIncludes;
};
/**
 * @function getAuthHeaders
 * @description **prepares authentication headers for API request to CMS.**
 * - currently only API key and custom header authentication are supported.
 * @returns {Promise<object>} promise of http header object.
 */
export const getAuthHeaders = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const credConfig = yield getAuthConfig();
    let authHeader = {};
    const apikey = (_a = credConfig === null || credConfig === void 0 ? void 0 : credConfig.apikey) === null || _a === void 0 ? void 0 : _a.key;
    const customAuth = credConfig === null || credConfig === void 0 ? void 0 : credConfig.customAuth;
    if (apikey) {
        authHeader = {
            Authorization: `Basic ${apikey}`,
        };
    }
    if (customAuth) {
        authHeader = Object.assign({}, customAuth);
    }
    return authHeader;
});
/**
 * @function getErrorMessage
 * @description **prepares error message from error code.**
 * - the error codes and messages are internal configuration at CMS toolkit.
 * - error codes and messages can be of http, validation or generic type.
 * @param {object } error - error code and message object.
 * @returns {string} error message.
 */
export const getErrorMessage = (error) => {
    const errorMessage = {
        code: error.code,
        message: error.message,
    };
    return JSON.stringify(errorMessage);
};
/**
 * @function configureAuth
 * @description **gets auth config and stores in localStorage.**
 * - auth config contains API key and username.
 * - currently only API key authentication is supported.
 * @param {object} authConfig - auth configuration from the caller.
 * @returns {Promise<undefined>} promise of undefined.
 * @example
 * const authConfig = {
 *   apikey: {
 *     key: "YmlrbWFoYXJhbmE6U29LNW5HNDBvM1E4WlBQXV5WWFUOE1mQlMweVNZHMVRyTTBRYkQwS3UwcUkzR1Nvc3QwVGt0N2l1dkxYdw==",
 *   },
 *   customAuth: {
 *    "X-Api-Key": "YmlrbWFoYXJhbmE6U29LNW5HNDBvM1E4WlBQXV5WWFUOE1mQlMweVNZHMVRyTTBRYkQwS3UwcUkzR1Nvc3QwVGt0N2l1dkxYdw==",
 *   },
 * };
 *
 * await configureAuth(authConfig);
 */
export const configureAuth = (authConfig) => __awaiter(void 0, void 0, void 0, function* () {
    yield AsyncStorage.setItem(Config.AUTH_CONFIG, JSON.stringify(authConfig));
});
/**
 * @function configureServer
 * @description **receives server config and stores in localStorage.**
 * - server config contains base URL of the CMS.
 * - only DRUPAL_SERVER_URL is mandatory.
 * @param {object} serverConfig - server configuration from the caller.
 * @returns {Promise<undefined>} promise of undefined.
 * @example
 * const serverConfig = {
 *   DRUPAL_SERVER_URL: "https://abc.pantheonsite.io",
 *   DRUPAL_SERVER_URL_MENU: "https://abc.pantheonsite.io",
 *   DRUPAL_FILE_SERVER_URL: "https://abc.pantheonsite.io",
 * };
 *
 * await configureServer(serverConfig);
 */
export const configureServer = (serverConfig) => __awaiter(void 0, void 0, void 0, function* () {
    yield AsyncStorage.setItem(Config.SERVER_CONFIG, JSON.stringify(serverConfig));
});
/**
 * @function configureContent
 * @description **receives contents config and stores in localStorage.**
 * - contents config contains all the content type and paragraph type specific field's machine names.
 * @param {object} contentConfig - content configuration from the caller.
 * @returns {Promise<undefined>} promise of undefined.
 * @example
 * const contentConfig = {
 *    metadata: {
 *      home: {
 *        machineNames: [
 *          {
 *            machineName: "field_content_cover_media",
 *            isReferenceType: false,
 *            isMediaType: true,
 *          },
 *          {
 *            machineName: "field_content_title",
 *            isReferenceType: false,
 *            isMediaType: false,
 *          },
 *          {
 *            machineName: "field_heros",
 *            isReferenceType: true,
 *            isMediaType: false,
 *          },
 *        ],
 *      },
 *    },
 *    paragraphs: {
 *      hero: {
 *        machineNames: [
 *          {
 *            machineName: "field_banners",
 *            isReferenceType: true,
 *            isMediaType: false,
 *          },
 *          {
 *            machineName: "field_description",
 *            isMediaType: false,
 *          },
 *          {
 *            machineName: "field_image",
 *            isMediaType: true,
 *          },
 *        ],
 *      },
 *    },
 *  };
 *
 *  await configureContent(contentConfig);
 */
export const configureContent = (contentConfig) => __awaiter(void 0, void 0, void 0, function* () {
    yield AsyncStorage.setItem(Config.CONTENT_CONFIG, JSON.stringify(contentConfig));
});
/**
 * @function configureStaticContent
 * @description **receives static contents config and stores in localStorage.**
 * - static contents config contains all the paragraphs used in the static content.
 * - in static content, a new paragraph type can not be added or removed .
 * @param {object} staticContentConfig - static content configuration from the caller.
 * @returns {Promise<undefined>} promise of undefined.
 * @example
 * const staticContentConfig = {
 *   page: ["hero", "row"],
 *   landing_page: ["hero", "row", "accordion", "card"],
 * };
 *
 * await configureStaticContent(staticContentConfig);
 */
export const configureStaticContent = (staticContentConfig) => __awaiter(void 0, void 0, void 0, function* () {
    yield AsyncStorage.setItem(Config.STATIC_CONTENT_CONFIG, JSON.stringify(staticContentConfig));
});
/**
 * @function getAuthConfig
 * @description **gets auth config from localStorage.**
 * - auth config contains API key and username.
 * - currently only API key authentication is supported.
 * - no parameter is required.
 * @returns {Promise<object>} promise of auth configuration from localStorage.
 */
export const getAuthConfig = () => __awaiter(void 0, void 0, void 0, function* () {
    var _b;
    let authConfig = {};
    authConfig = JSON.parse(((_b = (yield AsyncStorage.getItem(Config.AUTH_CONFIG))) !== null && _b !== void 0 ? _b : "") || "{}");
    if (Object.keys(authConfig).length === 0) {
        throw new Error(getErrorMessage(Errors.DTK002001));
    }
    return authConfig;
});
/**
 * @function getServerConfig
 * @description **gets server config from localStorage.**
 * - server config contains base URL of the CMS.
 * - no parameter is required.
 * @returns {Promise<object>} promise of server configuration from localStorage.
 */
export const getServerConfig = () => __awaiter(void 0, void 0, void 0, function* () {
    var _c;
    let serverConfig = { DRUPAL_SERVER_URL: "" };
    serverConfig = JSON.parse(((_c = (yield AsyncStorage.getItem(Config.SERVER_CONFIG))) !== null && _c !== void 0 ? _c : "") || "{}");
    if (Object.keys(serverConfig).length === 0) {
        throw new Error(getErrorMessage(Errors.DTK002002));
    }
    return serverConfig;
});
/**
 * @function getContentConfig
 * @description **gets content config from localStorage.**
 * - contents config contains all the content type and paragraph type specific field's machine names.
 * - no parameter is required.
 * @returns {Promise<object>} promise of content configuration from localStorage.
 */
export const getContentConfig = () => __awaiter(void 0, void 0, void 0, function* () {
    var _d;
    let contentConfig = { metadata: {}, paragraphs: {} };
    contentConfig = JSON.parse(((_d = (yield AsyncStorage.getItem(Config.CONTENT_CONFIG))) !== null && _d !== void 0 ? _d : "") || "{}");
    if (Object.keys(contentConfig).length === 0) {
        throw new Error(getErrorMessage(Errors.DTK002003));
    }
    return contentConfig;
});
/**
 * @function getStaticContentConfig
 * @description **gets static content config from localStorage.**
 * - static contents config contains all the paragraphs used in the static content.
 * - static content can not add or remove a new paragraph type.
 * - no parameter is required.
 * @returns {Promise<object>} promise of static content configuration from localStorage.
 */
export const getStaticContentConfig = () => __awaiter(void 0, void 0, void 0, function* () {
    var _e;
    let staticContentConfig = {};
    staticContentConfig = JSON.parse(((_e = (yield AsyncStorage.getItem(Config.STATIC_CONTENT_CONFIG))) !== null && _e !== void 0 ? _e : "") || "{}");
    if (Object.keys(staticContentConfig).length === 0) {
        throw new Error(getErrorMessage(Errors.DTK002004));
    }
    return staticContentConfig;
});
